import z from "zod";
import { scholarshipApi } from "@/lib/api/scholarship";
import { preCheckExistWrapper } from "@/lib/utils/func";
import { scholarshipTitleApi } from "@/lib/api/scholarship-title";
// import { majorCodes, majorCodesWithName } from "./shared";

export type tScholarshipSchema = z.infer<typeof scholarshipSchema>;

// Regex
const noWhitespaceNoSpecial = /^[A-Za-z0-9]+$/;
// const decimal0to10 = /^(10(\.0{1,2})?|[0-9](\.[0-9]{1,2})?)$/;
// const integer0to100 = /^([0-9]|[1-9][0-9]|100)$/;
// const moneyRegex = /^\d{1,3}(\.\d{3})*(\.\d+)?$/;

export const scholarshipSchema = z
	.object({
		id: z.string().optional(),
		major: z.string().nonempty("Bắt buộc nhập"),

		// *Năm học
		year: z.string().nonempty("Bắt buộc nhập").max(255, "Tối đa 255 ký tự"),

		// *Học kỳ
		semester: z.string().max(255, "Tối đa 255 ký tự").optional(),

		// *Mã sinh viên
		studentId: z.string().nonempty("Bắt buộc nhập").max(40, "Tối đa 40 ký tự").regex(noWhitespaceNoSpecial, "Không khoảng trắng, không ký tự đặc biệt"),

		// *Họ và tên
		studentName: z.string().nonempty("Bắt buộc nhập").max(255, "Tối đa 255 ký tự"),

		// *Ngày sinh
		dateOfBirth: z.date({ required_error: "Bắt buộc nhập" }).optional().refine((val) => {
			if (!val) return true;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const cutoff = new Date(today);
			cutoff.setDate(today.getDate() - 1);
			const dob = new Date(val);
			dob.setHours(0, 0, 0, 0);
			return dob <= cutoff;
		}, {
			message: "Ngày sinh không hợp lệ",
		}),

		// CCCD 8 hoặc 9 hoặc 12 chữ số
		cccd: z
			.string()
			.nonempty("Bắt buộc nhập")
			.refine((v) => !v || /^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(v), "CCCD sai định dạng"),
		// Điểm TB: 0–10, tối đa 2 thập phân
		// gpa: z
		// 	.string()
		// 	.optional()
		// 	.refine((v) => !v || decimal0to10.test(v), "Điểm TB phải từ 0-10 và tối đa 2 chữ số thập phân"),

		// Điểm RL: 0–100, số nguyên
		// conductScore: z
		// 	.string()
		// 	.optional()
		// 	.refine((v) => !v || integer0to100.test(v), "Điểm RL phải là số nguyên từ 0-100"),

		// Mã KT-CN
		scholarshipCode: z.string().max(255, "Tối đa 255 ký tự").optional(),

		// *Tên KT-CN
		scholarshipName: z.string().nonempty("Bắt buộc nhập").max(255, "Tối đa 255 ký tự"),

		// *Danh hiệu: Dropdown
		// title: z.enum(["Đạt danh hiệu Sinh viên Xuất sắc", "Đạt danh hiệu Sinh viên Giỏi", "Đạt danh hiệu Sinh viên Tiên tiến"], {
		// 	required_error: "Vui lòng chọn danh hiệu",
		// }),
		title: z.string().nonempty("Bắt buộc nhập").max(255, "Tối đa 255 ký tự"),

		// Số tiền KT-CN > 0 (int hoặc decimal)
		// amount: z
		// 	.string()
		// 	.optional()
		// 	.refine((v) => {
		// 		if (!v) return true;
		// 		if (!moneyRegex.test(v)) return false;
		// 		const normalized = v.replace(/\./g, ""); // bỏ dấu . phân tách
		// 		return parseFloat(normalized) > 0;
		// 	}, "Số tiền không hợp lệ"),

		// *Số quyết định: duy nhất trong DB và trong file → kiểm tra realtime ở UI
		certificateNo: z.string().nonempty("Bắt buộc nhập").max(255, "Tối đa 255 ký tự"),
		currentCertificateNo: z.string().optional(),

		// Mức PT
		// percentLevel: z.string().max(255, "Tối đa 255 ký tự").optional(),

		// Ghi chú
		notes: z.string().max(500, "Tối đa 500 ký tự").optional(),
	})
	.superRefine(async (data, ctx) => {
		const { certificateNo, currentCertificateNo, dateOfBirth } = data;

		// Check dateOfBirth is not empty
		if (!dateOfBirth) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Bắt buộc nhập",
				path: ["dateOfBirth"],
			});
		}

		await preCheckExistWrapper<string>(currentCertificateNo, certificateNo, async (value) => {
			const exists = await scholarshipApi.checkExist({ certificateNo: value });
			if (exists) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Số quyết định đã tồn tại",
					path: ["certificateNo"], // Chỉ định lỗi vào trường nào
				});
				return;
			}
		});
	});

//= Kiểm tra dữ liệu trước lúc import
export async function validateScholarshipImportData(importedScholarship: any[]) {
	// lần 1: thu thập để kiểm tra unique trong file
	const studentIdCounts = {} as any;
	const certificateCounts = {} as any;

	importedScholarship.forEach((item: any) => {
		studentIdCounts[item.studentId] = (studentIdCounts[item.studentId] || 0) + 1;
		certificateCounts[item.certificateNo] = (certificateCounts[item.certificateNo] || 0) + 1;
	});

	const { data } = await scholarshipTitleApi.getAll();
	const titles = data.data.map(item => item.titleName);

	// Regex chuẩn
	const regexNoSpecial = /^[A-Za-z0-9]+$/;
	const regexDate = /^(\d{2})\/(\d{2})\/(\d{4})$/;
	// const regexDecimal = /^\d+(\.\d{1,2})?$/;

	importedScholarship.forEach((item) => {
		const errors = [];

		// --- Năm học ---
		if (item.year) {
			if (!item.year.trim()) {
				errors.push("Năm học không được để trống");
			} else if (item.year.length > 20) {
				errors.push("Năm học không hợp lệ (tối đa 20 ký tự)");
			}
		} else {
			errors.push("Năm học không được để trống");
		}

		// ===== Tên ngành =====
		if (!item.major) {
			errors.push("Tên ngành không được để trống");
		}

		// --- Học kỳ ---
		if (item.semester.length > 255) {
			errors.push("Học kỳ không hợp lệ (tối đa 255 ký tự)");
		}

		// --- Mã sinh viên ---
		if (item.studentId) {
			if (!item.studentId) {
				errors.push("Mã sinh viên không được để trống");
			} else {
				if (item.studentId.length > 40) errors.push("Mã sinh viên tối đa 40 ký tự");
				if (!regexNoSpecial.test(item.studentId)) {
					errors.push("Mã sinh viên không chứa khoảng trắng hoặc ký tự đặc biệt");
				}
				if (studentIdCounts[item.studentId] > 1) {
					errors.push("Mã sinh viên phải duy nhất trong file");
				}
			}
		} else {
			errors.push("Mã sinh viên không được để trống");
		}

		// --- Họ và tên ---
		if (item.studentName) {
			if (!item.studentName) {
				errors.push("Họ và tên không được để trống");
			} else if (item.studentName.length > 255) {
				errors.push("Họ và tên tối đa 255 ký tự");
			}
		} else {
			errors.push("Họ và tên không được để trống");
		}

		// --- CCCD ---
		if (item.cccd) {
			if (!/^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{9}|[a-zA-Z0-9]{12})$/.test(item.cccd)) {
				errors.push("CCCD sai định dạng");
			}
		} else {
			errors.push("CCCD không được để trống");
		}

		// --- Ngày sinh ---
		if (item.dateOfBirth) {
			if (!regexDate.test(item.dateOfBirth)) {
				errors.push("Ngày sinh phải dạng dd/mm/yyyy");
			} else {
				// So sánh theo ngày (bỏ qua giờ/phút) để tránh lệch múi giờ
				const [dd, mm, yyyy] = item.dateOfBirth.split("/").map(Number);
				const dob = new Date(yyyy, (mm || 1) - 1, dd || 1);
				dob.setHours(0, 0, 0, 0);
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				if (dob >= today) {
					errors.push("Ngày sinh không hợp lệ");
				}
			}
		} else {
			errors.push("Ngày sinh không được để trống");
		}

		// --- Điểm TB ---
		// if (item.gpa) {
		// 	if (!regexDecimal.test(item.gpa.toString())) {
		// 		errors.push("Điểm TB phải là số 0-10, tối đa 2 chữ số thập phân");
		// 	} else {
		// 		const normalized = item.gpa.toString().replace(",", ".");
		// 		const num = parseFloat(normalized);
		// 		if (num < 0 || num > 10) {
		// 			errors.push("Điểm TB phải trong khoảng 0-10");
		// 		}
		// 		item.gpa = num.toFixed(2); // format 0.00
		// 	}
		// }

		// --- Điểm RL ---
		// if (item.conductScore) {
		// 	const score = Number(item.conductScore);

		// 	// parse không được hoặc không phải số nguyên
		// 	if (Number.isNaN(score) || !Number.isInteger(score)) {
		// 		errors.push("Điểm RL phải là số nguyên");
		// 	} else if (score < 0 || score > 100) {
		// 		errors.push("Điểm RL phải trong khoảng 0-100");
		// 	} else {
		// 		item.conductScore = String(score); // gán lại dạng số
		// 	}
		// }

		// --- Mã KT-CN ---
		if (item.scholarshipCode) {
			if (item.scholarshipCode.length > 255) {
				errors.push("Mã KT-CN tối đa 255 ký tự");
			}
		}

		// --- Tên KT-CN ---
		if (item.scholarshipName) {
			if (!item.scholarshipName.trim()) {
				errors.push("Tên KT-CN không được để trống");
			} else if (item.scholarshipName.length > 255) {
				errors.push("Tên KT-CN tối đa 255 ký tự");
			}
		} else {
			errors.push("Tên KT-CN không được để trống");
		}

		// --- Danh hiệu ---
		if (!item.title) {
			errors.push("Danh hiệu không được để trống");
		} else {
			// Kiểm tra danh hiệu có trong danh sách hay không
			if (!titles.includes(item.title)) {
				errors.push("Danh hiệu không hợp lệ");
			}
		}

		// --- Số tiền KT-CN ---
		// if (item.amount) {
		// 	const amountStr = item.amount.toString().trim();

		// 	if (!/^\d+$/.test(amountStr)) {
		// 		errors.push("Số tiền không hợp lệ");
		// 	} else {
		// 		const amount = Number(amountStr);

		// 		if (amount <= 0) {
		// 			errors.push("Số tiền phải lớn hơn 0");
		// 		} else {
		// 			item.amount = formatMoneyVN(amount);
		// 		}
		// 	}
		// }


		// --- Số quyết định ---
		if (item.certificateNo) {
			if (!item.certificateNo.trim()) {
				errors.push("Số quyết định không được để trống");
			} else if (item.certificateNo.length > 255) {
				errors.push("Số quyết định tối đa 255 ký tự");
			}
		} else {
			errors.push("Số quyết định không được để trống");
		}

		if (certificateCounts[item.certificateNo] > 1) {
			errors.push("Số quyết định phải duy nhất trong file");
		}

		// --- Mức PT ---
		// if (item.percentLevel && item.percentLevel.length > 255) {
		// 	errors.push("Mức PT tối đa 255 ký tự");
		// }

		// --- Ghi chú ---
		if (item.notes && item.notes.length > 500) {
			errors.push("Ghi chú tối đa 500 ký tự");
		}

		item.errors = errors;
	});

	return importedScholarship;
}
